import React from 'react';
import Seo from '../components/global/Seo';

/**
 * Public SMS Opt-In Disclosure page — https://nuvesystem.com/sms-opt-in
 *
 * Purpose: provides wireless carriers and The Campaign Registry (TCR) with a
 * publicly verifiable record of how end-users consent to receive SMS messages
 * from NuVe System (operated by NUVE SYSTEM LLC). Screenshots are served from
 * the public/ folder at the site root and referenced by absolute URL.
 */

const SCREENSHOTS = {
  phoneEntry: process.env.PUBLIC_URL + '/01_phone_entry.png',
  consentUnchecked: process.env.PUBLIC_URL + '/02_consent_unchecked.png',
  consentChecked: process.env.PUBLIC_URL + '/03_consent_checked.png',
  confirmationSms: process.env.PUBLIC_URL + '/04_confirmation_sms.png',
};

class SmsOptIn extends React.Component {
  handleBack = () => {
    if (this.props.history) {
      this.props.history.goBack();
    } else {
      window.history.back();
    }
  };

  render() {
    return (
      <div style={containerStyle}>
        <Seo
          title="SMS Opt-In Disclosure"
          description="NuVe System SMS opt-in disclosure: how customers consent to receive text messages, the types of messages sent, and how to opt out."
          path="/sms-opt-in"
        />
        {/* --- FIXED APP TOOLBAR --- */}
        <div style={toolbarStyle}>
          <button onClick={this.handleBack} style={iconButtonStyle}>
            ←
          </button>
          <div style={toolbarTitleStyle}>SMS Opt-In Disclosure</div>
          <div style={{ width: '40px' }}></div>
        </div>

        <div style={cardStyle}>
          {/* Header */}
          <header style={headerStyle}>
            <h1 style={titleStyle}>
              NuVe System — SMS Program &amp; Opt-In Disclosure
            </h1>
            <p style={{ ...dateStyle, fontStyle: 'italic' }}>
              Last Updated: October 3, 2026
            </p>
          </header>

          <div style={contentStyle}>
            {/* 1. Program overview + required disclosures */}
            <section style={sectionStyle}>
              <h2 style={headingStyle}>1. Program Overview</h2>
              <p>
                NuVe System (operated by NUVE SYSTEM LLC) is a membership and
                loyalty platform used by independently operated business
                locations. NuVe System sends two categories of SMS messages:
              </p>
              <ul style={listStyle}>
                <li>
                  <strong>Phone Verification (transactional):</strong> one-time
                  verification codes (OTP) used to confirm the customer's
                  identity, sent only when the customer requests verification at
                  the check-in tablet. One message per verification request.
                </li>
                <li>
                  <strong>Member Benefits &amp; Promotions (optional):</strong>{' '}
                  recurring promotional offers, loyalty rewards, special event
                  announcements, and business updates — sent{' '}
                  <strong>only</strong> to customers who separately and
                  expressly opt in.
                </li>
              </ul>
              <p>
                The two categories are presented as{' '}
                <strong>two separate consents</strong>, each with its own
                checkbox. Neither checkbox is pre-selected. Accepting phone
                verification does not opt a customer into promotional messages,
                and a customer may accept verification while declining
                promotions.
              </p>
              <div style={importantNoticeStyle}>
                <strong>
                  Message frequency varies. Message and data rates may apply.
                </strong>{' '}
                Reply <strong>STOP</strong> to opt out of promotional messages
                at any time. Reply <strong>HELP</strong> for help, or contact
                info@nuvesystem.com. Consent to promotional messages is{' '}
                <strong>not</strong> a condition of membership or of using NuVe
                System, and <strong>no SMS of any kind is required</strong> to
                become a member, earn points, or redeem them (see Section 2).
                Mobile phone numbers and SMS opt-in data are never shared with,
                sold to, or rented to third parties or affiliates for marketing
                purposes.
              </div>
            </section>

            {/* 2. SMS is never required to access the service */}
            <section style={sectionStyle}>
              <h2 style={headingStyle}>
                2. SMS Is Not Required to Use NuVe System
              </h2>
              <p>
                Customers are never required to opt into SMS in order to access
                the service. Both consents are optional in the sense that
                matters: a customer who does not wish to receive any text
                message can still become a member, earn points, and redeem
                points.
              </p>
              <ul style={listStyle}>
                <li>
                  <strong>Staff-assisted alternative:</strong> the self-service
                  tablet is one way to check in, not the only way. A customer
                  who declines SMS is checked in at the counter by a staff
                  member instead, with no verification text sent. This
                  alternative is stated in plain view on the consent screen
                  itself — not hidden in fine print — so the customer sees it
                  before deciding.
                </li>
                <li>
                  <strong>Promotional messages are independent:</strong>{' '}
                  declining the Member Benefits &amp; Promotions consent has no
                  effect on membership, points, rewards, pricing, or any other
                  part of the service.
                </li>
                <li>
                  <strong>Verification is transactional and one-time:</strong>{' '}
                  if a customer does choose SMS verification, it is a one-time
                  step. Once the number is verified, later check-ins send no
                  further messages.
                </li>
              </ul>
            </section>

            {/* 3. How opt-in happens */}
            <section style={sectionStyle}>
              <h2 style={headingStyle}>3. How End-Users Opt In</h2>
              <p>
                Consent is captured digitally, directly from the customer.
                During membership check-in or enrollment, after entering their
                phone number, the customer is shown a consent screen carrying
                two separate checkboxes — <strong>Phone Verification</strong>{' '}
                and <strong>Member Benefits &amp; Promotions</strong>.
              </p>
              <p>
                <strong>
                  Both checkboxes are empty when the screen opens. Neither is
                  pre-selected, pre-checked, or checked on the customer's
                  behalf.
                </strong>{' '}
                The customer must physically tap a box to check it, and the
                marketing box is never checked as a side effect of checking the
                verification box. A customer may check the verification box
                alone and continue; the promotional box can be left empty
                without blocking anything.
              </p>
              <p>
                Each opt-in is recorded with a UTC timestamp and location and
                stored as proof of consent. Promotional messages are sent only
                to customers whose record shows an affirmative marketing opt-in.
              </p>
            </section>

            {/* 4. Screenshots of the live opt-in flow */}
            <section style={sectionStyle}>
              <h2 style={headingStyle}>4. Screenshots of the Opt-In Process</h2>
              <p>
                The following screenshots are captured from the live NuVe System
                check-in flow, in order. They show the consent screen exactly as
                it opens — with <strong>both checkboxes empty</strong> and the
                ACCEPT &amp; CONTINUE button greyed out — followed by the
                affirmative opt-in and the confirmation message the customer
                receives. Customer phone numbers are masked for privacy.
              </p>

              <div style={screenshotGridStyle}>
                <figure style={screenshotFigureStyle}>
                  <img
                    src={SCREENSHOTS.phoneEntry}
                    alt="NuVe System check-in screen where the customer enters their phone number"
                    style={screenshotImgStyle}
                    loading="lazy"
                  />
                  <figcaption style={screenshotCaptionStyle}>
                    Step 1 — Membership check-in / phone number entry.
                  </figcaption>
                </figure>

                <figure style={screenshotFigureStyle}>
                  <img
                    src={SCREENSHOTS.consentUnchecked}
                    alt="Consent screen as it first opens, with both the Phone Verification and the Member Benefits and Promotions checkboxes empty and the continue button disabled"
                    style={screenshotImgStyle}
                    loading="lazy"
                  />
                  <figcaption style={screenshotCaptionStyle}>
                    Step 2 — The consent screen as it opens.{' '}
                    <strong>Both checkboxes are empty</strong> and ACCEPT &amp;
                    CONTINUE is disabled. Nothing is pre-selected. The notice
                    above the cards tells the customer they can decline SMS and
                    be checked in by staff instead.
                  </figcaption>
                </figure>

                <figure style={screenshotFigureStyle}>
                  <img
                    src={SCREENSHOTS.consentChecked}
                    alt="Consent screen showing the Member Benefits and Promotions marketing checkbox affirmatively checked by the customer"
                    style={screenshotImgStyle}
                    loading="lazy"
                  />
                  <figcaption style={screenshotCaptionStyle}>
                    Step 3 — Customer <strong>affirmatively taps</strong> each
                    box they wish to accept. Checking the optional marketing box
                    is a separate, deliberate action.
                  </figcaption>
                </figure>

                <figure style={screenshotFigureStyle}>
                  <img
                    src={SCREENSHOTS.confirmationSms}
                    alt="Opt-in confirmation text message received by the customer after opting in to promotional messages from NuVe System"
                    style={screenshotImgStyle}
                    loading="lazy"
                  />
                  <figcaption style={screenshotCaptionStyle}>
                    Step 4 — Opt-in confirmation message received by SMS after
                    opting in.
                  </figcaption>
                </figure>
              </div>
            </section>

            {/* 5. Sample messages */}
            <section style={sectionStyle}>
              <h2 style={headingStyle}>5. Example Messages</h2>
              <p>
                <strong>Verification (required consent):</strong>
              </p>
              <div style={smsBubbleStyle}>
                Your NuVe System verification code is: 123456. This code expires
                in 5 minutes.
              </div>
              <p>
                <strong>Promotional (optional consent only):</strong>
              </p>
              <div style={smsBubbleStyle}>
                NuVe System: Hi {'{first_name}'}, bonus play points are waiting
                for you at {'{location_name}'} this weekend! Reply STOP to opt
                out, HELP for help.
              </div>
            </section>

            {/* 6. Opt-out & help */}
            <section style={sectionStyle}>
              <h2 style={headingStyle}>6. Opt-Out and Support</h2>
              <ul style={listStyle}>
                <li>
                  <strong>STOP:</strong> Reply STOP to any promotional message
                  to cancel promotional messages. You will receive one
                  confirmation message and no further promotional messages.
                </li>
                <li>
                  <strong>HELP:</strong> Reply HELP to any message, or contact
                  info@nuvesystem.com for assistance.
                </li>
              </ul>
            </section>

            {/* 7. Links */}
            <section style={sectionStyle}>
              <h2 style={headingStyle}>7. Policies</h2>
              <p>
                <a href="/privacy-policy" style={linkStyle}>
                  Privacy Policy
                </a>
                {' · '}
                <a href="/terms-and-conditions" style={linkStyle}>
                  Terms and Conditions
                </a>
              </p>
              <div style={contactBoxStyle}>
                NUVE SYSTEM LLC
                <br />
                5900 Balcones Drive STE 100
                <br />
                Austin, TX 78731
                <br />
                United States
                <br />
                <br />
                info@nuvesystem.com
                <br />
                (346) 461-5222
              </div>
            </section>
          </div>
        </div>
      </div>
    );
  }
}

// ===== Page styles =====

const dateStyle = {
  color: '#777',
  fontSize: '0.95rem',
  fontWeight: '400',
};

const contentStyle = {
  textAlign: 'left',
};

const sectionStyle = {
  marginBottom: '35px',
};

const headingStyle = {
  color: '#001665',
  fontSize: '1.3rem',
  marginBottom: '15px',
  fontWeight: '700',
};

const listStyle = {
  paddingLeft: '25px',
  marginBottom: '15px',
};

const importantNoticeStyle = {
  backgroundColor: '#f8f9ff',
  borderLeft: '4px solid #001665',
  padding: '15px 20px',
  marginTop: '15px',
  fontSize: '0.95rem',
  fontStyle: 'italic',
};

const contactBoxStyle = {
  backgroundColor: '#f4f4f4',
  padding: '20px',
  borderRadius: '8px',
  marginTop: '10px',
  display: 'inline-block',
  minWidth: '250px',
};

const linkStyle = {
  color: '#1a237e',
  fontWeight: '600',
};

const toolbarStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  height: '60px',
  backgroundColor: '#001665',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 15px',
  zIndex: 1000,
  boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
};

const iconButtonStyle = {
  backgroundColor: 'transparent',
  color: 'white',
  border: 'none',
  fontSize: '24px',
  cursor: 'pointer',
  padding: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const toolbarTitleStyle = {
  color: 'white',
  fontSize: '18px',
  fontWeight: '600',
};

const containerStyle = {
  backgroundColor: '#ffffff',
  minHeight: '100vh',
  paddingTop: '80px',
  paddingBottom: '40px',
  paddingLeft: '15px',
  paddingRight: '15px',
  display: 'flex',
  justifyContent: 'center',
  fontFamily: '"Segoe UI", Roboto, Helvetica, Arial, sans-serif',
};

const cardStyle = {
  backgroundColor: '#ffffff',
  maxWidth: '900px',
  width: '100%',
  padding: '30px 20px',
  borderRadius: '12px',
  color: '#333',
  lineHeight: '1.7',
};

const headerStyle = {
  borderBottom: '3px solid #001665',
  marginBottom: '30px',
  paddingBottom: '15px',
};

const titleStyle = {
  color: '#001665',
  fontSize: '1.8rem',
  margin: '0 0 10px 0',
};

const screenshotGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
  gap: '20px',
  marginTop: '20px',
};

const screenshotFigureStyle = {
  margin: 0,
  border: '1px solid #c5cae9',
  borderRadius: '12px',
  overflow: 'hidden',
  backgroundColor: '#f7f8fc',
};

const screenshotImgStyle = {
  display: 'block',
  width: '100%',
  height: 'auto',
};

const screenshotCaptionStyle = {
  padding: '12px 14px',
  fontSize: '0.85rem',
  color: '#333',
  lineHeight: '1.45',
  margin: 0,
};

const smsBubbleStyle = {
  backgroundColor: '#e8eaf6',
  border: '1px solid #c5cae9',
  borderRadius: '14px',
  padding: '12px 16px',
  maxWidth: '440px',
  fontSize: '0.92rem',
  color: '#0a1128',
  marginBottom: '15px',
};

export default SmsOptIn;
